import type { IEmployeeLineAssignment } from "@/widgets/settings-users/users/interface/employee";

export const LINE_ASSIGNMENT_ERROR_KEYS = {
  lineRequired: "usersSettings.lineOfBusinessRequired",
  roleRequired: "usersSettings.lineOfBusinessRoleRequired",
} as const;

/** Inline errors of the lines/roles selector (values are translation keys). */
export interface ILineAssignmentErrors {
  /** Not tied to a line (e.g. no line chosen). */
  form?: string;
  /** Per line id. */
  lines: Record<string, string>;
}

export const EMPTY_LINE_ASSIGNMENT_ERRORS: ILineAssignmentErrors = { lines: {} };

export const hasLineAssignmentErrors = (errors: ILineAssignmentErrors): boolean =>
  !!errors.form || Object.keys(errors.lines).length > 0;

/**
 * Validates the assignments of a print house that has lines: at least one line
 * and a role for every line the actor can edit (`editableLineIds`). Assignments
 * outside the actor's authority are kept as-is by the server and not checked.
 */
export const validateLineAssignments = (
  assignments: ReadonlyArray<IEmployeeLineAssignment>,
  editableLineIds: ReadonlyArray<string>,
): ILineAssignmentErrors => {
  const errors: ILineAssignmentErrors = { lines: {} };
  if (!assignments || assignments.length === 0) {
    errors.form = LINE_ASSIGNMENT_ERROR_KEYS.lineRequired;
    return errors;
  }
  assignments.forEach((assignment) => {
    if (editableLineIds.includes(assignment.lineOfBusinessId) && !assignment.roleId) {
      errors.lines[assignment.lineOfBusinessId] =
        LINE_ASSIGNMENT_ERROR_KEYS.roleRequired;
    }
  });
  return errors;
};

/**
 * Payload entries: only the lines the actor may manage (assignments on other
 * lines are untouched by the server and must not be re-sent).
 */
export const toLinesPayload = (
  assignments: ReadonlyArray<IEmployeeLineAssignment>,
  editableLineIds: ReadonlyArray<string>,
): IEmployeeLineAssignment[] =>
  assignments
    .filter((assignment) => editableLineIds.includes(assignment.lineOfBusinessId))
    .map(({ lineOfBusinessId, roleId }) => ({ lineOfBusinessId, roleId }));
